/** Web 商品文案与整份设备配置编辑器；复用既有 FormRow / 多语言池 / Dialog。 */
import {
  CreateWebPaymentProductRequestSchema,
  UpdateWebPaymentProductRequestSchema,
  type WebPaymentProductResponse,
  type WebProductDevicePlatform,
  type WebProductVariantRequest,
  WebProductVariantRequestSchema,
} from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { webVariantRequest } from '~/data/adapters/web-payment-products-adapter';
import { useSysConfig } from '~/data/queries';
import { webPaymentProductsApi } from '~/hooks/use-web-payment-products';
import { Dialog, DialogBody } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { MultiLangFormRows } from '~/ui/MultiLangFormRows';
import { Select } from '~/ui/Select';
import { toast } from '~/ui/Toast';

export interface WebProductEditorProps {
  appId: string;
  product?: WebPaymentProductResponse;
  device?: WebProductDevicePlatform;
  onClose: () => void;
}
const EMPTY_VARIANT: WebProductVariantRequest = {
  vendor_product_id: '',
  stripe_price_id: '',
  subscription_period: null,
  coin_amount: '0',
  bonus_amount: '0',
  daily_grant_amount: '0',
  original_amount_minor_units: null,
  sort_order: 0,
  is_active: false,
};

export function WebPaymentProductEditor({
  appId,
  product,
  device,
  onClose,
}: WebProductEditorProps) {
  const isCreate = !product;
  const isVariant = device !== undefined;
  const current = product?.variants.find((v) => v.device_platform === device);
  // 只有已存变体本身无 Stripe 映射才提示原值下架；新建或清空有映射变体的输入不算。
  const unmappedHint =
    current?.vendor_product_id === null ? '无 Stripe 映射：不填写时只能保持原值下架' : undefined;
  const [type, setType] = useState<'CONSUMABLE' | 'SUBSCRIPTION'>(
    product?.product_type ?? 'CONSUMABLE',
  );
  const [accessKey, setAccessKey] = useState('');
  const [name, setName] = useState(product?.name ?? '');
  const [title, setTitle] = useState<Record<string, string>>(product?.title_i18n ?? { en: '' });
  const [promotion, setPromotion] = useState<Record<string, string>>(product?.promotion_i18n ?? {});
  const [variant, setVariant] = useState<WebProductVariantRequest>(
    current
      ? webVariantRequest(current)
      : {
          ...EMPTY_VARIANT,
          subscription_period: product?.product_type === 'SUBSCRIPTION' ? 'MONTHLY' : null,
        },
  );
  const [formError, setFormError] = useState('');
  const sysConfig = useSysConfig();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      const metadata = {
        name,
        title_i18n: title,
        promotion_i18n: Object.keys(promotion).length > 0 ? promotion : null,
      };
      const schema = isCreate
        ? CreateWebPaymentProductRequestSchema
        : isVariant
          ? WebProductVariantRequestSchema
          : UpdateWebPaymentProductRequestSchema;
      const body = isCreate
        ? {
            ...metadata,
            access_level_id: accessKey,
            product_type: type,
            subscription_effect: type === 'SUBSCRIPTION' ? 'VIP_READING' : null,
            default_variant: { ...variant, is_active: false },
          }
        : isVariant
          ? variant
          : metadata;
      const parsed = schema.safeParse(body);
      if (!parsed.success)
        throw new Error(
          parsed.error.issues.map((v) => `${v.path.join('.')}: ${v.message}`).join('；'),
        );
      if (isCreate)
        return webPaymentProductsApi.create(
          appId,
          CreateWebPaymentProductRequestSchema.parse(body),
        );
      if (isVariant)
        return webPaymentProductsApi.replaceVariant(
          product.id,
          device,
          WebProductVariantRequestSchema.parse(body),
        );
      return webPaymentProductsApi.update(
        product.id,
        UpdateWebPaymentProductRequestSchema.parse(body),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['web-payment-products', appId] });
      toast('Web 商品配置已保存', { tone: 'pos' });
      onClose();
    },
    onError: (error) => setFormError(error.message),
  });
  const patch = (input: Partial<WebProductVariantRequest>) =>
    setVariant((v) => ({ ...v, ...input }));
  const pending = mutation.isPending;
  const close = () => {
    if (!pending) onClose();
  };
  return (
    <Dialog
      title={isCreate ? '新建 Web 商品' : isVariant ? `${device} 完整配置` : '编辑 Web 商品'}
      subtitle={
        isCreate
          ? '商品与 DEFAULT 同时创建为草稿'
          : isVariant
            ? '整份保存；下架覆盖不会回落默认，移除覆盖后才回落'
            : '商品身份与效果创建后不可改'
      }
      onClose={close}
      footer={
        <>
          <button type="button" className="btn btn-ghost" disabled={pending} onClick={close}>
            取消
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={pending}
            onClick={() => {
              setFormError('');
              mutation.mutate();
            }}
          >
            {pending ? '保存中…' : '保存'}
          </button>
        </>
      }
    >
      <DialogBody>
        {formError && (
          <div role="alert" className="chip chip-neg">
            {formError}
          </div>
        )}
        {isCreate && (
          <>
            <FormRow label="商品类型">
              <Select
                value={type}
                disabled={pending}
                onChange={(e) => {
                  const value = e.target.value as typeof type;
                  setType(value);
                  patch({
                    subscription_period: value === 'SUBSCRIPTION' ? 'MONTHLY' : null,
                    coin_amount: '0',
                    bonus_amount: '0',
                  });
                }}
              >
                <option value="CONSUMABLE">金币包</option>
                <option value="SUBSCRIPTION">VIP 阅读订阅</option>
              </Select>
            </FormRow>
            <FormRow label="Access key" required hint="自家商品键，无需 Adapty 登记">
              <input
                aria-label="Access key"
                className="input"
                value={accessKey}
                disabled={pending}
                onChange={(e) => setAccessKey(e.target.value)}
              />
            </FormRow>
          </>
        )}
        {!isVariant && (
          <>
            <FormRow label="识别名" required>
              <input
                aria-label="识别名"
                className="input"
                value={name}
                disabled={pending}
                onChange={(e) => setName(e.target.value)}
              />
            </FormRow>
            <MultiLangFormRows
              label="商品标题"
              requiredLangs={['en']}
              value={title}
              onChange={setTitle}
              availableLangs={sysConfig.data.languages
                .filter((l) => l.enabled)
                .map((l) => l.language_code)}
            />
            <MultiLangFormRows
              label="宣传文字"
              requiredLangs={[]}
              value={promotion}
              onChange={setPromotion}
              availableLangs={sysConfig.data.languages
                .filter((l) => l.enabled)
                .map((l) => l.language_code)}
            />
            <button
              type="button"
              className="btn btn-ghost"
              disabled={pending}
              onClick={() => setPromotion({})}
            >
              清除宣传文字
            </button>
          </>
        )}
        {(isCreate || isVariant) && (
          <fieldset disabled={pending} style={{ border: 0, margin: 0, padding: 0 }}>
            <FormRow label="Stripe Product" required hint="每个设备配置使用独立 Product">
              <input
                aria-label="Stripe Product"
                className="input"
                value={variant.vendor_product_id ?? ''}
                placeholder={unmappedHint}
                onChange={(e) => patch({ vendor_product_id: e.target.value || null })}
              />
            </FormRow>
            <FormRow label="Stripe Price" required hint="价格与币种从 Stripe 回读，不能手工设置">
              <input
                aria-label="Stripe Price"
                className="input"
                value={variant.stripe_price_id ?? ''}
                placeholder={unmappedHint}
                onChange={(e) => patch({ stripe_price_id: e.target.value || null })}
              />
            </FormRow>
            {current && (
              <FormRow label="当前标价">
                <span>
                  {current.amount_minor_units} 最小货币单位 · {current.currency}
                </span>
              </FormRow>
            )}
            {type === 'SUBSCRIPTION' ? (
              <FormRow label="订阅周期">
                <Select
                  value={variant.subscription_period ?? 'MONTHLY'}
                  onChange={(e) =>
                    patch({ subscription_period: e.target.value as 'WEEKLY' | 'MONTHLY' })
                  }
                >
                  <option value="WEEKLY">每周</option>
                  <option value="MONTHLY">每月</option>
                </Select>
              </FormRow>
            ) : (
              <>
                <FormRow label="金币" required>
                  <input
                    aria-label="金币"
                    className="input"
                    inputMode="numeric"
                    value={variant.coin_amount}
                    onChange={(e) => patch({ coin_amount: e.target.value })}
                  />
                </FormRow>
                <FormRow label="BONUS">
                  <input
                    aria-label="BONUS"
                    className="input"
                    inputMode="numeric"
                    value={variant.bonus_amount}
                    onChange={(e) => patch({ bonus_amount: e.target.value })}
                  />
                </FormRow>
              </>
            )}
            <FormRow label="划线原价" hint="可空；同币种最小货币单位，必须高于实际价">
              <input
                aria-label="划线原价"
                className="input"
                inputMode="numeric"
                value={variant.original_amount_minor_units ?? ''}
                onChange={(e) => patch({ original_amount_minor_units: e.target.value || null })}
              />
            </FormRow>
            <FormRow label="排序">
              <input
                aria-label="排序"
                className="input"
                type="number"
                value={variant.sort_order}
                onChange={(e) => patch({ sort_order: Number(e.target.value) })}
              />
            </FormRow>
            {isVariant && (
              <FormRow label="上架">
                <Select
                  value={variant.is_active ? 'true' : 'false'}
                  onChange={(e) => patch({ is_active: e.target.value === 'true' })}
                >
                  <option value="false">下架</option>
                  <option value="true">上架（须 Stripe 核验通过）</option>
                </Select>
              </FormRow>
            )}
          </fieldset>
        )}
      </DialogBody>
    </Dialog>
  );
}
